/**
 * linkifyString - Full markdown rendering with automatic URL detection
 *
 * This uses ReactMarkdown with remarkGfm to render text with full markdown support,
 * including automatic URL detection and linking.
 *
 * NOTE: This interprets ALL markdown syntax, not just URLs. Text like "*bold*" will
 * render as bold, "#heading" as a heading, etc.
 *
 * For URL-only linkification without markdown interpretation, use LinkifyTextCustomField
 * from ./linkify-text-custom-field.jsx instead.
 */

import * as React from 'react';
import ReactMarkdown from 'react-markdown';

import remarkGfm from 'remark-gfm';

/**
 * Safely render text with automatic link detection using react-markdown.
 * This replaces the dangerous dangerouslySetInnerHTML approach with safe React components.
 *
 * WARNING: This interprets markdown syntax. For plain text with only URL linkification,
 * use LinkifyTextCustomField instead.
 */
export function linkifyString(string?: string | null): React.ReactElement {
    if (!string) {
        return <span />;
    }

    return (
        <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
                a: ({...props}) => (
                    <a className='underline' target='_blank' rel='noopener noreferrer' {...props} />
                ),
                // Rendering paragraphs as spans to maintain inline behavior
                p: ({...props}) => <span {...props} />,
            }}
        >
            {string}
        </ReactMarkdown>
    );
}

/**
 * Create a markdown link with custom URL
 * This safely creates [text](url) markdown instead of dangerous HTML injection
 */
export function linkifyWithCustomLink(text: string, link: string): React.ReactElement {
    const markdownLink = `[${text}](${link})`;

    return linkifyString(markdownLink);
}
